import { HTTPService } from "../services/httpService";
import { convertToUTC } from "./convert_timezone";

const API = new HTTPService();

/**
 * Service Actions Utility
 *
 * Shared functions for starting/finishing services used by:
 * - requestimates.jsx (full job view)
 * - MyWork.jsx (technician view)
 *
 * These are lower-level functions that handle the database updates.
 * The calling component is responsible for:
 * - UI alerts/notifications
 * - SMS notifications
 * - Navigation after action
 * - Materials collection modal (for finish)
 */

/**
 * Update a service's state in request_services table
 * @param {Object} params
 * @param {number} params.requestId - The request ID
 * @param {number} params.serviceId - The service ID
 * @param {string} params.serviceState - New state: "started" | "finished"
 * @param {Object} [params.materialsData] - Optional materials data for finish
 */
export const updateServiceState = async ({ requestId, serviceId, serviceState, materialsData = null }) => {
  const requestService = {
    requestId,
    serviceId,
    serviceState,
    serviceDate: convertToUTC(new Date()).format("YYYY-MM-DD HH:mm:ss"),
    materials_data: materialsData
  };

  const apiEndpoint = `/requestServiceState/${requestId}`;
  await API.put(apiEndpoint, requestService);
};

/**
 * Update the started_at or finished_at timestamp on a service
 * Uses the PATCH endpoint for partial updates
 * @param {Object} params
 * @param {number} params.requestServiceId - The request_service row PK (id)
 * @param {number} params.requestId - The request ID (legacy fallback)
 * @param {number} params.serviceId - The service ID (legacy fallback)
 * @param {string} params.action - "start" | "finish"
 * @param {number} params.userId - ID of user performing the action
 */
export const updateServiceTimestamps = async ({ requestServiceId, requestId, serviceId, action, userId }) => {
  const timestamp = convertToUTC(new Date()).format("YYYY-MM-DD HH:mm:ss");

  const updateData = action === 'start'
    ? { started_at: timestamp, started_by: userId }
    : { finished_at: timestamp, finished_by: userId };

  if (requestServiceId) {
    await API.patch(`/requestService/row/${requestServiceId}`, updateData);
  } else {
    // Legacy fallback for older calls without the row PK
    await API.patch(`/requestService/${requestId}/${serviceId}`, updateData);
  }
};

/**
 * Update the installer calendar entry for a service
 * @param {Object} params
 * @param {number} params.requestId - The request ID
 * @param {number} params.serviceId - The service ID (null for all services)
 * @param {number} params.installerId - The installer company ID
 * @param {string} params.workStatus - New status: "started" | "finished"
 */
export const updateCalendarEntry = async ({ requestId, serviceId, installerId, workStatus }) => {
  const calendarEntry = {
    requestId,
    installerId,
    serviceId,
    workDate: convertToUTC(new Date()).format("YYYY-MM-DD HH:mm:ss"),
    workStatus
  };

  const apiEndpoint = `/installerCalendars/${requestId}`;
  await API.put(apiEndpoint, calendarEntry);
};

/**
 * Add an audit record to request_state table
 * @param {Object} params
 * @param {number} params.requestId - The request ID
 * @param {string} params.status - The state/action: "start" | "finish" | etc
 * @param {number} [params.serviceId] - Optional service ID
 * @param {number} params.userId - ID of user making the change
 * @param {string} params.userType - Type of user: "installer" | "dealer"
 * @param {string} [params.reasonCode] - Optional reason code
 * @param {string} [params.reasonNotes] - Optional reason notes
 */
export const addAuditRecord = async ({
  requestId,
  status,
  serviceId = null,
  userId,
  userType,
  reasonCode = null,
  reasonNotes = null
}) => {
  const state = {
    requestId,
    stateDate: convertToUTC(new Date()).format("YYYY-MM-DD HH:mm:ss"),
    requestStatus: status,
    serviceId,
    changedByUserId: userId,
    changedByUserType: userType,
    reasonCode,
    reasonNotes
  };

  await API.post('/requestState', state);
};

/**
 * Start a service - updates state, timestamps, calendar, and audit
 *
 * @param {Object} params
 * @param {number} params.requestId - The request ID
 * @param {number} params.serviceId - The service ID
 * @param {number} params.installerId - The installer company ID
 * @param {number} params.userId - ID of user starting the service
 * @param {string} params.userType - Type of user: "installer"
 * @returns {Promise<void>}
 */
export const startService = async ({ requestId, serviceId, installerId, userId, userType }) => {
  console.log('📝 serviceActions.startService - Step 1: updateServiceState');
  // Update service state to "started"
  await updateServiceState({ requestId, serviceId, serviceState: 'started' });

  console.log('📝 serviceActions.startService - Step 2: updateServiceTimestamps');
  // Record started_at timestamp and started_by user
  await updateServiceTimestamps({ requestId, serviceId, action: 'start', userId });

  console.log('📝 serviceActions.startService - Step 3: updateCalendarEntry');
  // Update calendar entry
  await updateCalendarEntry({ requestId, serviceId, installerId, workStatus: 'started' });

  console.log('📝 serviceActions.startService - Step 4: addAuditRecord');
  // Add audit record
  await addAuditRecord({
    requestId,
    status: 'start',
    serviceId,
    userId,
    userType
  });

  console.log('📝 serviceActions.startService - All steps completed');
};

/**
 * Finish a service - updates state, timestamps, calendar, and audit
 *
 * @param {Object} params
 * @param {number} params.requestId - The request ID
 * @param {number} params.serviceId - The service ID
 * @param {number} params.installerId - The installer company ID
 * @param {number} params.userId - ID of user finishing the service
 * @param {string} params.userType - Type of user: "installer"
 * @param {Object} [params.materialsData] - Optional materials data collected
 * @returns {Promise<void>}
 */
export const finishService = async ({
  requestId,
  serviceId,
  installerId,
  userId,
  userType,
  materialsData = null
}) => {
  // Update service state to "finished" with optional materials data
  await updateServiceState({ requestId, serviceId, serviceState: 'finished', materialsData });

  // Record finished_at timestamp and finished_by user
  await updateServiceTimestamps({ requestId, serviceId, action: 'finish', userId });

  // Update calendar entry
  await updateCalendarEntry({ requestId, serviceId, installerId, workStatus: 'finished' });

  // Add audit record
  await addAuditRecord({
    requestId,
    status: 'finish',
    serviceId,
    userId,
    userType
  });
};

/**
 * Update the request's current state
 * Used when transitioning the overall job (e.g., received -> started)
 * @param {Object} params
 * @param {number} params.requestId - The request ID
 * @param {string} params.currentState - New state: "started" | "finished" | etc
 */
export const updateRequestState = async ({ requestId, currentState }) => {
  // Use PATCH to update just the current_state field
  const apiEndpoint = `/requestimates/${requestId}`;
  await API.patch(apiEndpoint, { currentState });
};

/**
 * Update the request offer state
 * Used to keep the offer in sync with the request state
 * @param {Object} params
 * @param {number} params.requestId - The request ID
 * @param {number} params.installerId - The installer company ID
 * @param {string} params.currentStatus - New status: "started" | "finished" | etc
 */
export const updateRequestOfferState = async ({ requestId, installerId, currentStatus }) => {
  const offer = {
    requestId,
    installerId,
    currentStatus,
    dateUpdated: convertToUTC(new Date()).format("YYYY-MM-DD HH:mm:ss")
  };
  const apiEndpoint = `/requestoffered/state/${requestId}`;
  await API.put(apiEndpoint, offer);
};

/**
 * Send an SMS notification
 * @param {Object} params
 * @param {string} params.messageType - "User" | "Customer"
 * @param {string} params.userType - "dealer" | "customer" | "installer"
 * @param {number} params.recipientId - The recipient's ID
 * @param {string} params.text - The message text
 */
export const sendSmsNotification = async ({ messageType, userType, recipientId, text }) => {
  const data = {
    messageType,
    messagePlatform: "SMS",
    userType,
    id: recipientId,
    text
  };
  await API.post("/messages/push", data);
};

/**
 * Check if all services in a job are finished
 * @param {Array} services - Array of service objects
 * @returns {boolean} True if all services have service_state === "finished"
 */
export const checkAllServicesComplete = (services) => {
  if (!services || services.length === 0) return false;
  return services.every(s => s.service_state === 'finished');
};

/**
 * Get the count of finished services
 * @param {Array} services - Array of service objects
 * @returns {Object} { finished: number, total: number }
 */
export const getServiceProgress = (services) => {
  if (!services || services.length === 0) return { finished: 0, total: 0 };
  const finished = services.filter(s => s.service_state === 'finished').length;
  return { finished, total: services.length };
};

const serviceActions = {
  startService,
  finishService,
  checkAllServicesComplete,
  getServiceProgress,
  updateServiceState,
  updateServiceTimestamps,
  updateCalendarEntry,
  addAuditRecord,
  updateRequestState,
  updateRequestOfferState,
  sendSmsNotification
};

export default serviceActions;
